import { MARKETING_LOCALES, MarketingLanguage } from "../../../marketing-content/marketingLocales";
import { getLocalizedMarketingHref, getMarketingPageLanguage } from "../../../marketing-content/marketingPageTranslations";
import { getMarketingTranslations } from "../../../marketing-content/marketingTranslations";
import React from "react";
import { Box, Flex, Grid, Image, Link, SimpleGrid, Text } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import JungleLogo from "../../../images/JungleLogo";
import { landing } from "../landingTokens";
import { trackLandingCallToActionClick } from "../handoff/appHandoff";
import { useAppUrl } from "../handoff/useAppUrl";
import { useLandingTheme } from "../LandingThemeContext";
import { useLandingLanguage } from "../LandingLanguageContext";
import { MarketingLanguageSelector } from "../../marketing-site/MarketingLanguageSelector";
import { getLandingCopy } from "../landingCopy";
import { CONTAINER_WIDTH, PAGE_SIDE_PADDING } from "./themed/ThemedLayout";
import { AppStoreTextLinks, WEB_APP_CTA_CLASS_NAME } from "./AppStoreCallToAction";

type FooterLanguage = MarketingLanguage;

// Resolve page identities against actual publication, including staged clusters.
type FooterLink = { label: string; path: string; language: MarketingLanguage; isFallback: boolean };

type FooterLinkLists = {
  useCaseLinks: FooterLink[];
  companyLinks: FooterLink[];
  moreLinks: FooterLink[];
  // Themed versions fold the untitled third column into Company.
  themedCompanyLinks: FooterLink[];
  // Themed versions list the product pages on their own row: the use-case, alternative and conversion
  // pages have no other link from the home page, the blog or each other's sections.
  themedProductLinks: FooterLink[];
};

function buildFooterLinkLists(language: FooterLanguage): FooterLinkLists {
  const labels = getMarketingTranslations(language).footer;
  const pageLink = (label: string, englishPath: string): FooterLink => {
    const path = getLocalizedMarketingHref(englishPath, language);
    const availableLanguage = getMarketingPageLanguage(path) ?? "en";
    return { label, path, language: availableLanguage, isFallback: availableLanguage !== language };
  };
  const useCaseLinks: FooterLink[] = [
    pageLink(labels.aiMcqMaker, "/use-cases/multiple-choice-question-maker"),
    pageLink(labels.aiQuizMaker, "/use-cases/ai-quiz-maker"),
    pageLink(labels.aiImageOcclusion, "/use-cases/ai-image-occlusion"),
  ];
  const companyLinks: FooterLink[] = [
    pageLink(labels.blog, "/blog"),
    pageLink(labels.privacyPolicy, "/privacy-policy"),
    pageLink(labels.termsOfService, "/terms-of-service"),
  ];
  const moreLinks: FooterLink[] = [
    pageLink(labels.pricing, "/pricing"),
    pageLink(labels.about, "/about"),
    pageLink(labels.affiliate, "/affiliate"),
  ];
  const sectionLinks: FooterLink[] = [
    pageLink(labels.useCasesTitle, "/use-cases"),
    pageLink(labels.alternatives, "/alternatives"),
    pageLink(labels.converters, "/conversions"),
  ];
  return {
    useCaseLinks,
    companyLinks,
    moreLinks,
    themedCompanyLinks: [...moreLinks, ...companyLinks],
    themedProductLinks: [...useCaseLinks, ...sectionLinks],
  };
}

const SOCIAL_LINKS = [
  { icon: "social-1.png", href: "https://www.tiktok.com/@junglelearning_", label: "TikTok" },
  { icon: "social-2.png", href: "https://www.instagram.com/junglelearning_", label: "Instagram" },
  { icon: "social-3.png", href: "https://www.youtube.com/@junglelearning", label: "YouTube" },
  { icon: "social-4.png", href: "https://twitter.com/junglelearning_", label: "Twitter" },
];

type FooterVariant = "original" | "themed";

function useFooterStyles(variant: FooterVariant) {
  const theme = useLandingTheme();
  const isLightFooter = theme.color.footerBg === "#FFFFFF";
  const textStyle = { fontFamily: theme.font.body, fontSize: variant === "original" ? "18px" : "15px" } as const;
  const linkStyle = {
    ...textStyle,
    color: theme.color.footerLink,
    _hover: { color: isLightFooter ? theme.color.accent : "white", textDecoration: "none" },
  } as const;
  const titleColor = isLightFooter ? theme.color.heading : "rgba(255, 255, 255, 0.8)";
  return { theme, isLightFooter, textStyle, linkStyle, titleColor };
}

function FooterColumnTitle({ variant, children }: { variant: FooterVariant; children: string }) {
  const { textStyle, titleColor } = useFooterStyles(variant);
  return (
    <Text {...textStyle} fontWeight={700} color={titleColor}>
      {children}
    </Text>
  );
}

const FooterLinkList = React.memo(function FooterLinkList({
  variant,
  links,
}: {
  variant: FooterVariant;
  links: FooterLink[];
}) {
  const { linkStyle } = useFooterStyles(variant);
  return (
    <>
      {links.map((footerLink) => (
        <Link key={footerLink.path} as={RouterLink} to={footerLink.path} hrefLang={MARKETING_LOCALES[footerLink.language].hreflang} {...linkStyle}>
          {footerLink.label}
          {footerLink.isFallback && <> <Box as="bdi" lang={MARKETING_LOCALES[footerLink.language].htmlLang} fontSize="0.85em">({MARKETING_LOCALES[footerLink.language].name})</Box></>}
        </Link>
      ))}
    </>
  );
});

function SocialIcons({ variant }: { variant: FooterVariant }) {
  const { isLightFooter } = useFooterStyles(variant);
  return (
    <Flex gap="10px">
      {SOCIAL_LINKS.map((socialLink) => (
        <Link key={socialLink.label} href={socialLink.href} isExternal aria-label={socialLink.label}>
          <Image
            src={`/images/landing/${socialLink.icon}`}
            alt=""
            w="24px"
            h="24px"
            filter={isLightFooter ? "invert(1)" : "none"}
          />
        </Link>
      ))}
    </Flex>
  );
}

function LoginAndSignUpLinks({ variant, language }: { variant: FooterVariant; language: FooterLanguage }) {
  const { textStyle, linkStyle, titleColor } = useFooterStyles(variant);
  const labels = getMarketingTranslations(language).footer;
  const loginHref = useAppUrl(landing.loginUrl);
  const signUpHref = useAppUrl(landing.signUpUrl);
  return (
    <Flex gap="24px">
      <Link
        href={loginHref}
        onClick={() => trackLandingCallToActionClick("footer_login")}
        {...textStyle}
        color={titleColor}
        _hover={linkStyle._hover}
      >
        {labels.login}
      </Link>
      <Link
        href={signUpHref}
        className={WEB_APP_CTA_CLASS_NAME}
        onClick={() => trackLandingCallToActionClick("footer_sign_up")}
        {...linkStyle}
      >
        {labels.signUp}
      </Link>
      {/* Phone visitors from a Meta ad get the store link instead of Sign Up; Login stays on the web. */}
      <AppStoreTextLinks placement="footer" onlyAfterHydration {...linkStyle} />
    </Flex>
  );
}

function FooterLogo({ variant }: { variant: FooterVariant }) {
  const { isLightFooter } = useFooterStyles(variant);
  return isLightFooter ? (
    <JungleLogo height={56} />
  ) : (
    <Image src="/images/landing/footer-logo.png" alt="Jungle" h="80px" w="136px" objectFit="contain" />
  );
}

// `language` is optional: left out, the footer follows the page's LandingLanguageProvider.
// `showLanguageSelector` is for the home page, whose nav has no language selector: one copy, after both the
// desktop and the phone layout (both are in the HTML, so a copy in each would double the 14 language links).
export default function LandingFooter({
  variant,
  language: languageFromProp,
  showLanguageSelector = false,
}: {
  variant: FooterVariant;
  language?: FooterLanguage;
  showLanguageSelector?: boolean;
}) {
  const languageFromContext = useLandingLanguage();
  const language = languageFromProp ?? languageFromContext;
  const { theme, isLightFooter, textStyle } = useFooterStyles(variant);
  const emailColor = isLightFooter ? theme.color.text : theme.color.footerText;
  const isOriginal = variant === "original";
  const labels = getMarketingTranslations(language).footer;
  const { useCaseLinks, companyLinks, moreLinks, themedCompanyLinks, themedProductLinks } = buildFooterLinkLists(language);

  if (isOriginal) {
    return (
      <Box as="footer" bg={theme.color.footerBg}>
        {/* The illustration's own gradient sky sits on the page background and its hills end in the footer green. */}
        <Box bg={theme.color.pageBg}>
          <Image
            loading="lazy"
            src="/images/landing/monkey-capybara.webp"
            alt={getLandingCopy(language).closingCallToAction.capybaraAndMonkeyIllustrationAlt}
            w="100%"
            display="block"
          />
        </Box>

        {/* Tablet + desktop: four columns */}
        <Box
          display={{ base: "none", md: "block" }}
          maxW="1096px"
          mx="auto"
          px={{ md: "46px", lg: 0 }}
          pt="72px"
          pb="24px"
        >
          {/* Column widths measured from Framer at tablet (820px) and desktop (1440px). */}
          <Grid templateColumns={{ md: "215px 157px 185px auto", lg: "297px 289px 232px auto" }} alignItems="start">
            <Flex direction="column" gap="16px">
              <FooterLogo variant={variant} />
              <Text {...textStyle} color={emailColor} whiteSpace="nowrap">
                hello@jungleai.com
              </Text>
              <LoginAndSignUpLinks variant={variant} language={language} />
              <SocialIcons variant={variant} />
            </Flex>
            <Flex direction="column" gap="10px">
              <FooterColumnTitle variant={variant}>{labels.useCasesTitle}</FooterColumnTitle>
              <FooterLinkList variant={variant} links={useCaseLinks} />
            </Flex>
            <Flex direction="column" gap="10px">
              <FooterColumnTitle variant={variant}>{labels.companyTitle}</FooterColumnTitle>
              <FooterLinkList variant={variant} links={companyLinks} />
            </Flex>
            {/* No title, so pad down to align its first link with the titled columns' first links. */}
            <Flex direction="column" gap="10px" pt="39px">
              <FooterLinkList variant={variant} links={moreLinks} />
            </Flex>
          </Grid>
        </Box>

        {/* Phone: everything centered in one column */}
        <Flex
          display={{ base: "flex", md: "none" }}
          direction="column"
          align="center"
          px="20px"
          pt="40px"
          pb="72px"
          gap="16px"
        >
          <FooterLogo variant={variant} />
          <Text {...textStyle} color={emailColor}>
            hello@jungleai.com
          </Text>
          <LoginAndSignUpLinks variant={variant} language={language} />
          <Flex direction="column" align="center" gap="10px" mt="16px">
            <FooterColumnTitle variant={variant}>{labels.useCasesTitle}</FooterColumnTitle>
            <FooterLinkList variant={variant} links={useCaseLinks} />
          </Flex>
          <Flex direction="column" align="center" gap="10px" mt="16px" w="100%">
            <FooterColumnTitle variant={variant}>{labels.companyTitle}</FooterColumnTitle>
            <SimpleGrid columns={2} w="100%" maxW="350px" px="28px" spacingY="10px" spacingX="20px">
              <Flex direction="column" gap="10px">
                <FooterLinkList variant={variant} links={companyLinks} />
              </Flex>
              <Flex direction="column" gap="10px">
                <FooterLinkList variant={variant} links={moreLinks} />
              </Flex>
            </SimpleGrid>
          </Flex>
          <Box mt="16px">
            <SocialIcons variant={variant} />
          </Box>
        </Flex>
        {showLanguageSelector && <MarketingLanguageSelector language={language} footerColor={emailColor} />}
      </Box>
    );
  }

  const ruleColor = isLightFooter ? theme.color.border : "rgba(255, 255, 255, 0.12)";
  return (
    <Box as="footer" bg={theme.color.footerBg} borderTop={`1px solid ${ruleColor}`} position="relative">
      <Box
        maxW={CONTAINER_WIDTH}
        mx="auto"
        px={PAGE_SIDE_PADDING}
        pt={{ base: "48px", md: "64px" }}
        pb={{ base: "48px", md: "56px" }}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "center", md: "flex-start" }}
          gap="40px"
        >
          <Flex
            direction="column"
            gap="16px"
            align={{ base: "center", md: "flex-start" }}
            textAlign={{ base: "center", md: "left" }}
          >
            <FooterLogo variant={variant} />
            <Text {...textStyle} color={emailColor}>
              hello@jungleai.com
            </Text>
            <LoginAndSignUpLinks variant={variant} language={language} />
            <SocialIcons variant={variant} />
          </Flex>
          <Flex direction="column" gap="16px" maxW="720px" pt={{ md: "8px" }}>
            <Flex wrap="wrap" justify={{ base: "center", md: "flex-end" }} columnGap="28px" rowGap="12px">
              <FooterLinkList variant={variant} links={themedProductLinks} />
            </Flex>
            <Flex wrap="wrap" justify={{ base: "center", md: "flex-end" }} columnGap="28px" rowGap="12px">
              <FooterLinkList variant={variant} links={themedCompanyLinks} />
            </Flex>
          </Flex>
        </Flex>
      </Box>
      {showLanguageSelector && <MarketingLanguageSelector language={language} footerColor={emailColor} />}
    </Box>
  );
}
